// "Send replacement" — the draft screen.
//
// A replacement is a free-of-charge duplicate of an order, raised when the
// customer's parcel was lost, damaged or short. This screen is the DRAFT:
// nothing is written until Save & send replacement. The order it creates
// lands as 'unfulfilled' and is picked, packed and shipped like any other,
// but at £0.00 — so no monetary values are shown or editable here.
//
// Every line starts UNTICKED on purpose. A replacement is usually a subset
// of the original order (one broken item out of five), and defaulting to
// "all of it" is the expensive mistake to make by reflex. The Add all
// items link is there for the genuine whole-order case.
//
// The ONE exception is lost-parcel mode (the `parcel` prop, opened from a
// shipped parcel in the Orders detail modal). There the operator has
// already said which box went missing, so the list is narrowed to that
// box's contents and all of it starts ticked — reproducing the parcel IS
// the whole job, and re-picking it line by line would only invite
// mistakes.
//
// The OTHER exception is flagged mode (the `flagged` prop, opened from the
// Flagged list's Resolve menu, 2026-10-08). The flag already says which
// units shipped short, so exactly those start ticked, and the save also
// closes the flag in the same BE transaction (resolve_flag) — one confirm
// instead of Admin Orders → back to Flagged → a zero Refund to clear it.
//
// Quantities are hard-capped at whatever is free in that warehouse right
// now (`available` from the draft endpoint), because the warehouse cannot
// pick what is not there. The server re-checks on save — stock moves while
// the operator is choosing — and a 409 re-renders these rows with the
// server's figures.
import { useState, useEffect, useCallback, useMemo } from "react";
import { Minus, Plus, Package, X, PackagePlus } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { resolveAttribution } from "./resolve-api";
import type { DispatchOrder, ReplacementDraftLine } from "../../types/Dispatch";

// One shipped parcel, narrowed down to what the draft screen needs.
// Passed when the operator is replacing a LOST PARCEL rather than raising
// a replacement against the whole order.
export type ReplacementParcelScope = {
    // "Parcel 2/3" — already localised by the caller, shown in the intro.
    label: string;
    tracking_number: string;
    // What packing recorded in this parcel (order_label_items). Keyed by
    // sku only: there is no line reference on a label item.
    items: { sku: string; quantity: number }[];
};

type Props = {
    order: DispatchOrder;
    onClose: () => void;
    // Fires after the replacement is created, with its REP number, so the
    // Orders page can refresh its list and re-open the original's detail.
    onCreated: (replacementNumber: string) => void;
    // Lost-parcel mode. Set and the draft is narrowed to this parcel's
    // contents, all ticked by default — see scopeToParcel below.
    parcel?: ReplacementParcelScope;
    // Flagged mode. `outstanding` is what the flag says shipped short per
    // line (missing + damaged + wrong); those units start ticked, capped
    // by free stock, and the save sends resolve_flag so the BE closes the
    // flag in the same transaction. Mutually exclusive with `parcel`.
    flagged?: { outstanding: { line_position: number; count: number }[] };
    // Flagged mode only: the BE answered 409 not_flagged — the order was
    // resolved elsewhere while the modal was open. Nothing was created;
    // the list should refresh.
    onConflict?: () => void;
};

// line_position → chosen quantity. Absent = not selected.
type Selection = Map<number, number>;

// Every line that CAN be replaced, at its full available quantity. Lines
// with no free stock are skipped rather than added at zero. Shared by the
// Add all items link and the lost-parcel default.
const selectEverything = (lines: ReplacementDraftLine[]): Selection => {
    const next: Selection = new Map();
    for (const line of lines) {
        const cap = Math.min(line.quantity, line.available);
        if (cap > 0) next.set(line.line_position, cap);
    }
    return next;
};

// Narrow the order's draft lines to what ONE parcel actually contained.
//
// A label item records a sku and a quantity, never a line_position, so a
// sku that appears on two order lines is matched by walking the lines in
// position order and consuming the parcel's quantity across them — the
// same first-fit split the Orders detail modal uses for its shipped /
// unshipped column. `quantity` is rewritten to the quantity IN THIS
// PARCEL, which is what caps the stepper: a parcel of one cannot be
// replaced by three even if three were ordered.
const scopeToParcel = (
    lines: ReplacementDraftLine[],
    items: ReplacementParcelScope["items"],
): ReplacementDraftLine[] => {
    const pool = new Map<string, number>();
    for (const it of items) pool.set(it.sku, (pool.get(it.sku) ?? 0) + it.quantity);
    const scoped: ReplacementDraftLine[] = [];
    for (const line of lines) {
        const left = pool.get(line.sku) ?? 0;
        if (left <= 0) continue;
        const inParcel = Math.min(left, line.quantity);
        pool.set(line.sku, left - inParcel);
        scoped.push({ ...line, quantity: inParcel });
    }
    return scoped;
};

// Flagged mode's default: each short line at its shortfall, never more
// than is free. A line whose sku has no free stock is left unticked and
// the row says so — the leader falls back to Refund for that one.
const selectOutstanding = (
    lines: ReplacementDraftLine[],
    outstanding: { line_position: number; count: number }[],
): Selection => {
    const next: Selection = new Map();
    const shortBy = new Map(outstanding.map(o => [o.line_position, o.count]));
    for (const line of lines) {
        const short = shortBy.get(line.line_position) ?? 0;
        const cap = Math.min(short, line.quantity, line.available);
        if (cap > 0) next.set(line.line_position, cap);
    }
    return next;
};

const ReplacementOrderModal = ({ order, onClose, onCreated, parcel, flagged, onConflict }: Props) => {
    const { t } = useTranslation();
    const [lines, setLines] = useState<ReplacementDraftLine[] | null>(null);
    const [selection, setSelection] = useState<Selection>(new Map());
    const [loadError, setLoadError] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/orders/${encodeURIComponent(order.order_id)}/replacement/draft`,
                    {},
                );
                if (!res.ok) { if (alive) setLoadError(true); return; }
                const data = await res.json();
                if (!alive) return;
                const all: ReplacementDraftLine[] = Array.isArray(data.lines) ? data.lines : [];
                // Lost parcel: only that parcel's contents are on offer, and
                // they start TICKED. The whole parcel is gone, so "all of it"
                // is the right default here — the opposite of the
                // whole-order case, where a replacement is usually a subset.
                const scoped = parcel ? scopeToParcel(all, parcel.items) : all;
                setLines(scoped);
                if (parcel) setSelection(selectEverything(scoped));
                else if (flagged) setSelection(selectOutstanding(scoped, flagged.outstanding));
            } catch {
                if (alive) setLoadError(true);
            }
        })();
        return () => { alive = false; };
    }, [order.order_id, parcel, flagged]);

    // The most a line may be replaced by: never more than was ordered, and
    // never more than is free in the warehouse.
    const capOf = (line: ReplacementDraftLine) => Math.min(line.quantity, line.available);

    const toggle = (line: ReplacementDraftLine) => {
        const cap = capOf(line);
        if (cap <= 0) return;
        setSelection(prev => {
            const next = new Map(prev);
            if (next.has(line.line_position)) next.delete(line.line_position);
            // Ticking defaults to ONE unit, not the whole line — same
            // reasoning as the unticked default.
            else next.set(line.line_position, 1);
            return next;
        });
    };

    const step = (line: ReplacementDraftLine, delta: number) => {
        const cap = capOf(line);
        setSelection(prev => {
            const current = prev.get(line.line_position);
            if (current === undefined) return prev;
            const next = new Map(prev);
            next.set(line.line_position, Math.min(cap, Math.max(1, current + delta)));
            return next;
        });
    };

    const addAll = useCallback(() => {
        if (!lines) return;
        setSelection(selectEverything(lines));
    }, [lines]);

    const removeAll = useCallback(() => setSelection(new Map()), []);

    const selectedUnits = useMemo(
        () => [...selection.values()].reduce((sum, n) => sum + n, 0),
        [selection],
    );

    const save = async () => {
        if (selection.size === 0 || saving) return;
        setSaving(true);
        try {
            const res = await apiFetch(
                `/orders/${encodeURIComponent(order.order_id)}/replacement`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        lines: [...selection.entries()].map(([line_position, quantity]) => ({
                            line_position, quantity,
                        })),
                        // Flagged mode: close the flag in the same transaction,
                        // attributed to the signed-in leader like a resolve.
                        ...(flagged ? { resolve_flag: true, ...resolveAttribution() } : {}),
                    }),
                },
            );
            const data = await res.json().catch(() => ({} as Record<string, unknown>));
            if (flagged && res.status === 409 && data?.code === "not_flagged") {
                // Resolved elsewhere while the modal was open. The BE rolled
                // back, so no replacement exists either — refresh, don't retry.
                toast.error(t("resolve_conflict"));
                onConflict?.();
                return;
            }
            if (res.status === 409 && data?.code === "insufficient_stock") {
                // Stock moved while the operator was choosing. Patch the
                // affected rows with the server's figures and clamp the
                // selection down, rather than just refusing.
                const missing = Array.isArray(data.missing) ? data.missing : [];
                const bySku = new Map(
                    missing.map((m: { sku: string; available: number }) => [m.sku, m.available]),
                );
                setLines(prev => prev?.map(l => (
                    bySku.has(l.sku) ? { ...l, available: Number(bySku.get(l.sku)) } : l
                )) ?? prev);
                setSelection(prev => {
                    const next = new Map(prev);
                    for (const l of lines ?? []) {
                        if (!bySku.has(l.sku)) continue;
                        const cap = Math.min(l.quantity, Number(bySku.get(l.sku)));
                        if (!next.has(l.line_position)) continue;
                        if (cap <= 0) next.delete(l.line_position);
                        else next.set(l.line_position, Math.min(next.get(l.line_position)!, cap));
                    }
                    return next;
                });
                toast.error(t("replacement_insufficient_stock"));
                return;
            }
            if (!res.ok) {
                toast.error((data?.message as string) || t("replacement_failed"));
                return;
            }
            onCreated(String(data.order_number ?? ""));
        } catch {
            toast.error(t("replacement_failed"));
        } finally {
            setSaving(false);
        }
    };

    const addr = order.delivery_address;

    return (
        <div className="modal-overlay" onClick={() => { if (!saving) onClose(); }}>
            <div
                className="modal-card dispatch-replacement-modal"
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-card-header">
                    <h2>{t("send_replacement")}</h2>
                    <button className="modal-close" onClick={onClose} disabled={saving} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <p className="dispatch-replacement-intro">
                        {parcel
                            ? t("replacement_intro_parcel", {
                                order: order.order_number,
                                parcel: parcel.label,
                            })
                            : flagged
                                ? t("replacement_intro_flagged", { order: order.order_number })
                                : t("replacement_intro", { order: order.order_number })}
                    </p>

                    {/* Where it's going + what it's worth. Read-only: a
                        replacement always ships to the original address, and
                        always at zero. */}
                    <div className="dispatch-replacement-summary">
                        <div>
                            <span className="dispatch-replacement-label">{t("col_customer")}</span>
                            <span>{order.customer?.name || "—"}</span>
                        </div>
                        <div>
                            <span className="dispatch-replacement-label">{t("delivery_address")}</span>
                            <span>
                                {[addr?.line_1, addr?.city, addr?.postcode].filter(Boolean).join(", ") || "—"}
                            </span>
                        </div>
                        <div>
                            <span className="dispatch-replacement-label">{t("warehouse")}</span>
                            <span>{order.warehouse || "—"}</span>
                        </div>
                        {parcel && (
                            <div>
                                <span className="dispatch-replacement-label">{t("col_tracking_number")}</span>
                                <span className="dispatch-replacement-tracking">{parcel.tracking_number}</span>
                            </div>
                        )}
                        <div>
                            <span className="dispatch-replacement-label">{t("col_value")}</span>
                            <span className="dispatch-replacement-free">{t("replacement_no_charge")}</span>
                        </div>
                    </div>

                    {loadError && (
                        <p className="dispatch-replacement-error">{t("replacement_draft_failed")}</p>
                    )}

                    {lines && lines.length > 0 && (
                        <>
                            <div className="dispatch-replacement-list-head">
                                <span>{t("items")}</span>
                                <span className="dispatch-replacement-bulk">
                                    <button
                                        type="button"
                                        className="dispatch-replacement-add-all"
                                        onClick={addAll}
                                        disabled={saving}
                                    >
                                        {t("replacement_add_all_items")}
                                    </button>
                                    <button
                                        type="button"
                                        className="dispatch-replacement-add-all"
                                        onClick={removeAll}
                                        disabled={saving || selection.size === 0}
                                    >
                                        {t("replacement_remove_all_items")}
                                    </button>
                                </span>
                            </div>
                            <ul className="dispatch-replacement-lines">
                                {lines.map(line => {
                                    const cap = capOf(line);
                                    const chosen = selection.get(line.line_position);
                                    const selected = chosen !== undefined;
                                    const src = line.thumbnail_url || line.image_url;
                                    return (
                                        <li
                                            key={line.line_position}
                                            className={`dispatch-replacement-line${cap <= 0 ? " dispatch-replacement-line--none" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                disabled={saving || cap <= 0}
                                                onChange={() => toggle(line)}
                                                aria-label={line.title || line.sku}
                                            />
                                            <span className="dispatch-replacement-thumb" aria-hidden="true">
                                                {src ? (
                                                    <img
                                                        src={src}
                                                        alt=""
                                                        loading="lazy"
                                                        onError={e => { e.currentTarget.style.display = "none"; }}
                                                    />
                                                ) : (
                                                    <Package size={20} />
                                                )}
                                            </span>
                                            <span className="dispatch-replacement-info">
                                                <span className="dispatch-replacement-title">{line.title || line.sku}</span>
                                                <span className="dispatch-replacement-sku">{line.sku}</span>
                                                <span className="dispatch-replacement-stock">
                                                    {cap > 0
                                                        ? t(parcel
                                                            ? "replacement_free_stock_parcel"
                                                            : (line.refunded ?? 0) > 0
                                                                ? "replacement_free_stock_refunded"
                                                                : "replacement_free_stock", {
                                                            n: String(line.available),
                                                            ordered: String(line.quantity),
                                                            refunded: String(line.refunded ?? 0),
                                                        })
                                                        : t("replacement_no_stock")}
                                                    {flagged && (() => {
                                                        const short = flagged.outstanding
                                                            .find(o => o.line_position === line.line_position)?.count ?? 0;
                                                        return short > 0
                                                            ? ` · ${t("replacement_line_short", { n: String(short) })}`
                                                            : null;
                                                    })()}
                                                </span>
                                            </span>
                                            <span className="dispatch-replacement-stepper">
                                                <button
                                                    type="button"
                                                    onClick={() => step(line, -1)}
                                                    disabled={saving || !selected || (chosen ?? 0) <= 1}
                                                    aria-label="-1"
                                                >
                                                    <Minus size={16} />
                                                </button>
                                                <span className="dispatch-replacement-qty">{chosen ?? 0}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => step(line, 1)}
                                                    disabled={saving || !selected || (chosen ?? 0) >= cap}
                                                    aria-label="+1"
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}

                    {lines && lines.length === 0 && (
                        <p className="dispatch-replacement-error">
                            {t(parcel ? "replacement_no_parcel_lines" : "replacement_no_lines")}
                        </p>
                    )}

                    {/* Flagged mode: say what the save does to the ORIGINAL,
                        mirroring the refund modal's result line, so the
                        leader isn't surprised that the flag disappears. */}
                    {flagged && lines && lines.length > 0 && (
                        <p className="dispatch-flagged-action-result">
                            {t("replacement_flag_resolve_note")}
                        </p>
                    )}

                    <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                        <button className="btn btn-ghost dispatch-modal-btn" onClick={onClose} disabled={saving}>
                            {t("cancel")}
                        </button>
                        <button
                            className="btn btn-success dispatch-modal-btn"
                            onClick={save}
                            disabled={saving || selection.size === 0}
                        >
                            <PackagePlus size={14} />
                            {saving
                                ? t("saving")
                                : t("replacement_save_and_send", { n: String(selectedUnits) })}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReplacementOrderModal;
